import { getJSON, postJSON } from 'common/services/api'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import type { Funder, FunderContractType } from '../types/invoicingRates'
import {
  FUNDER_QUERY_KEY,
  FUNDERS_CREATE_QUERY_KEY,
  FUNDERS_DELETE_QUERY_KEY,
  FUNDERS_QUERY_KEY,
  FUNDERS_UPDATE_QUERY_KEY,
  FUNDERS_URL
} from './queryConstants'

export interface FunderAddressDto {
  buildingName?: string
  line1?: string
  line2?: string
  city?: string
  country?: string
  postcode?: string
}

export interface FunderContractDto {
  _id?: string
  name: string
  invoicingRateCardInitialId: string
  notes?: string
  deleted?: boolean
}

export interface CreateFunderDto {
  name: string
  contractType: FunderContractType
  address?: FunderAddressDto
  email?: string
  isPrivate?: boolean
  contracts?: FunderContractDto[]
}

export type UpdateFunderDto = CreateFunderDto

export const useGetFunders = () => {
  return useQuery<Funder[]>([FUNDERS_QUERY_KEY], async () => {
    const response = await getJSON({ url: FUNDERS_URL, isRota: true })
    return response
  })
}

export const useGetFunder = (id: string) => {
  return useQuery<Funder>(
    [FUNDER_QUERY_KEY, id],
    async () => {
      const response = await getJSON({
        url: `${FUNDERS_URL}/${id}`,
        isRota: true
      })
      return response
    },
    { enabled: !!id && id !== 'new' }
  )
}

export const useCreateFunder = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: createFunder, isLoading } = useMutation<
    Funder,
    unknown,
    CreateFunderDto,
    unknown
  >(
    [FUNDERS_CREATE_QUERY_KEY],
    (dto) =>
      postJSON({ url: FUNDERS_URL, method: 'POST', json: dto, isRota: true }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([FUNDERS_QUERY_KEY])
      }
    }
  )

  return { createFunder, isLoading }
}

export const useUpdateFunder = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateFunder, isLoading } = useMutation<
    Funder,
    unknown,
    { id: string; dto: UpdateFunderDto },
    unknown
  >(
    [FUNDERS_UPDATE_QUERY_KEY],
    ({ id, dto }) =>
      postJSON({
        url: `${FUNDERS_URL}/${id}`,
        method: 'PUT',
        json: dto,
        isRota: true
      }),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries([FUNDERS_QUERY_KEY])
        queryClient.invalidateQueries([FUNDER_QUERY_KEY, id])
      }
    }
  )

  return { updateFunder, isLoading }
}

export const useDeleteFunder = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: deleteFunder, isLoading } = useMutation<
    void,
    unknown,
    string,
    unknown
  >(
    [FUNDERS_DELETE_QUERY_KEY],
    (id) =>
      postJSON({ url: `${FUNDERS_URL}/${id}`, method: 'DELETE', isRota: true }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([FUNDERS_QUERY_KEY])
      }
    }
  )

  return { deleteFunder, isLoading }
}
